import { useMemo } from "react";

import type { BranchNodeType } from "@citrine/resources/branch";
import { BRANCH_NODE_TYPES } from "@citrine/resources/branch";
import { isTableData } from "@citrine/resources/data-source";
import { DesignSpaceClient } from "@citrine/resources/design-space";
import { DesignWorkflowClient } from "@citrine/resources/design-workflow";
import { DesignWorkflowExecutionClient } from "@citrine/resources/design-workflow-execution";
import { GEMTableClient } from "@citrine/resources/gem-table";
import {
  useTrainingData,
  useTrainingDataDescription,
} from "@citrine/resources/predictor/utils";
import { PredictorVersionClient } from "@citrine/resources/predictor-versions";

import * as sx from "./branch-navigation.sx";
import type { BranchStepProps } from "./branch-step";
import { BranchStep } from "./branch-step";
import { ProjectToolbar } from "./project-toolbar";

export type BranchSteps = Record<
  BranchNodeType,
  Omit<BranchStepProps, "nodeType">
>;

function BranchNavigationView({ steps }: { steps?: BranchSteps }) {
  return steps ? (
    <sx.Steps data-testid="branch-steps">
      {BRANCH_NODE_TYPES.map((nodeType) => (
        <BranchStep key={nodeType} {...steps[nodeType]} nodeType={nodeType} />
      ))}
    </sx.Steps>
  ) : null;
}

function useWorkspaceExecutions(
  projectId: string,
  workspaceId: string,
  executionId?: string
) {
  const designWorkflowExecutionClient =
    DesignWorkflowExecutionClient.getInstance(projectId, workspaceId);
  const executionsQuery = designWorkflowExecutionClient.useFindAll(
    {},
    { enabled: !executionId && Boolean(workspaceId) }
  );

  const executionQuery = designWorkflowExecutionClient.useGet(executionId);

  return useMemo(
    () =>
      executionId && executionQuery.data
        ? [executionQuery.data]
        : executionsQuery.data,
    [executionId, executionQuery.data, executionsQuery.data]
  );
}

export function BranchNavigation({
  projectId,
  workspaceId,
  executionId,
  stepOverrides,
}: {
  projectId: string;
  workspaceId: string;
  executionId?: string;
  stepOverrides?: Partial<BranchSteps>;
}) {
  const workspaceQuery =
    DesignWorkflowClient.getInstance(projectId).useGet(workspaceId);

  const predictorQuery = PredictorVersionClient.getInstance(
    projectId,
    workspaceQuery.data?.predictor_id ?? ""
  ).useGet(
    workspaceQuery.data?.predictor_version?.toString() ?? "most_recent",
    {
      enabled: Boolean(workspaceQuery.data?.predictor_id),
    }
  );

  const designSpaceQuery = DesignSpaceClient.getInstance(projectId).useGet(
    workspaceQuery.data?.design_space_id ?? undefined
  );
  const executions = useWorkspaceExecutions(
    projectId,
    workspaceId,
    executionId
  );

  const trainingData = useTrainingData(projectId, workspaceQuery.data);
  const tableMapQuery = GEMTableClient.getInstance(projectId).useMapAll();
  const { name: trainingDataDescription, version: trainingDataVersion } =
    useTrainingDataDescription(trainingData, tableMapQuery.data);

  const trainingDataIdentifier =
    trainingData?.length === 1 ? trainingData[0] : undefined;

  const steps = useMemo(() => {
    const params = `?${new URLSearchParams({ workspace: workspaceId })}`;
    const predictorParams = `?${new URLSearchParams({
      workspace: workspaceId,
      version:
        predictorQuery.data?.metadata.version?.toString() ?? "most_recent",
    })}`;

    const execution = executions?.length === 1 ? executions[0] : undefined;

    return {
      data: {
        label: trainingDataDescription,
        complete: Boolean(
          workspaceQuery.data?.data_source_id ||
          workspaceQuery.data?.predictor_id
        ),
        to: isTableData(trainingDataIdentifier)
          ? `/projects/${projectId}/data/tables/${trainingDataIdentifier.table_id}/${trainingDataIdentifier.table_version}${params}`
          : !trainingDataIdentifier && !workspaceQuery.data?.predictor_id
            ? `/projects/${projectId}/data/query/new${params}`
            : undefined,
        version: trainingDataVersion,
      },
      predictor: {
        label: predictorQuery.data?.data.name,
        complete: Boolean(workspaceQuery.data?.predictor_id),
        to: workspaceQuery.data?.predictor_id
          ? `/projects/${projectId}/predictors/${workspaceQuery.data?.predictor_id}${predictorParams}`
          : `/projects/${projectId}/predictors${params}`,
        version: predictorQuery.data?.metadata.version,
      },
      design: {
        label: designSpaceQuery.data?.data.name,
        complete: Boolean(workspaceQuery.data?.design_space_id),
        to: workspaceQuery.data?.design_space_id
          ? `/projects/${projectId}/search-spaces/${workspaceQuery.data?.design_space_id}${params}`
          : workspaceQuery.data?.predictor_id
            ? `/projects/${projectId}/search-spaces${params}`
            : undefined,
      },
      candidate: {
        label: execution ? (
          <sx.Objective
            // rendered in a list, React warns about needing a unique key
            key="candidate-label"
            objective={execution.score.objectives[0]}
            baseline={execution.score.baselines?.[0]}
            tooltip={false}
          />
        ) : undefined,
        tooltip: execution?.score.objectives?.length ? (
          <>
            {execution.score.objectives.map((objective, index) => (
              <sx.Objective
                key={index}
                objective={objective}
                baseline={execution.score.baselines?.[index]}
                tooltip={false}
              />
            ))}
          </>
        ) : undefined,
        complete: Boolean(executions?.length),
        to: execution?.id
          ? `/projects/${projectId}/workspaces/${workspaceId}/executions/${execution.id}`
          : undefined,
      },
      ...stepOverrides,
    };
  }, [
    workspaceId,
    predictorQuery.data?.metadata.version,
    predictorQuery.data?.data.name,
    executions,
    trainingDataDescription,
    workspaceQuery.data?.data_source_id,
    workspaceQuery.data?.predictor_id,
    workspaceQuery.data?.design_space_id,
    trainingDataIdentifier,
    projectId,
    trainingDataVersion,
    designSpaceQuery.data?.data.name,
    stepOverrides,
  ]);

  return (
    <ProjectToolbar projectId={projectId} workspaceId={workspaceId}>
      <BranchNavigationView steps={steps} />
    </ProjectToolbar>
  );
}
